/**
 * Admin Ops dashboard / live map / queues / ride timeline (S112; M12). Scope-isolated by construction: every
 * view filters rides/drivers to the operator's active scope, so a city-scoped operator can never see another
 * city's data (the S111 scope + this filter enforce the boundary). KPIs + freshness are computed from the
 * scoped snapshots; queues group rides by dispatch state; manual dispatch/reassign go to the real S092
 * operator endpoint (server-audited); the ride timeline is the durable read an OPERATOR is authorized for
 * (D364 — see `loadRideTimeline`); and the live view recovers over the realtime ops channel via the S101
 * `RealtimeClient`.
 */
import { type OpsApi, type OpsCancelInput, type OpsCancelResult, type RideSupportApi, type RideSupportDetail, type RideSupportTimelineEntry, type ManualDispatchResult, type OpsDashboardView, type ApiError, type OperatingMarketView } from '@vello/api-client';
import { type Result } from '@vello/core';
import { countryOfMarket, marketsOfRegion } from './scope.js';
import { type OperatorScope } from './shell.js';

export interface RideSnapshot {
  readonly rideId: string;
  readonly status: string;
  readonly scope: OperatorScope;
  /** ISO-8601 of the last update (for freshness). */
  readonly at: string;
}

export interface DriverSnapshot {
  readonly driverId: string;
  readonly online: boolean;
  readonly scope: OperatorScope;
  readonly at: string;
}

// ---- scope isolation -----------------------------------------------------------------------------

function inScope(itemScope: OperatorScope, scope: OperatorScope, markets: readonly OperatingMarketView[]): boolean {
  if (scope.country !== undefined && itemScope.country !== scope.country) return false;
  if (scope.city !== undefined && itemScope.city !== scope.city) return false;
  // A state: the item's city must be one of the markets the catalog places in it (no city, or an unlisted one, is outside - fail closed).
  if (scope.city === undefined && scope.region !== undefined) {
    if (itemScope.city === undefined) return false;
    return marketsOfRegion(scope.region, markets).some((m) => m.id === itemScope.city);
  }
  return true;
}

/**
 * Filter any scoped items to the operator's active scope — a city operator never sees another city, and a state operator
 * sees only the markets of that state (`markets` is the catalog that places them; a state it cannot place keeps nothing).
 */
export function filterByScope<T extends { readonly scope: OperatorScope }>(items: readonly T[], scope: OperatorScope, markets: readonly OperatingMarketView[] = []): readonly T[] {
  return items.filter((i) => inScope(i.scope, scope, markets));
}

// ---- KPIs + freshness ----------------------------------------------------------------------------

export function freshnessSeconds(at: string, now: string): number {
  const d = Date.parse(now) - Date.parse(at);
  return Number.isNaN(d) ? Number.POSITIVE_INFINITY : Math.max(0, Math.round(d / 1000));
}

// D401: `OpsKpis`/`computeKpis` computed dashboard numbers from snapshots no screen ever held — the real
// Overview reads `GET /v1/ops/dashboard`. Removed with its test rather than kept as a second source of KPIs.

// ---- queues + timeline ---------------------------------------------------------------------------

export interface OpsQueues {
  readonly searching: readonly RideSnapshot[];
  readonly waiting: readonly RideSnapshot[];
}

export function buildQueues(rides: readonly RideSnapshot[], scope: OperatorScope, markets: readonly OperatingMarketView[] = []): OpsQueues {
  const scoped = filterByScope(rides, scope, markets);
  return {
    searching: scoped.filter((r) => /search|request/i.test(r.status)),
    waiting: scoped.filter((r) => /wait/i.test(r.status)),
  };
}

// ---- the operator's ride timeline (D364) ---------------------------------------------------------

/**
 * A ride's event log as an OPERATOR reads it. W2-CONSOLES (D364): the live map, dispatch and the case
 * cross-link used to call `GET /v1/rides/:rideId/timeline`, which is **participant-only** — the server answers
 * FORBIDDEN to anyone who is not that ride's rider or assigned driver, so every operator (SUPER_ADMIN included)
 * got a 403 and the screens showed a raw error code. The operator read is the D358 ride-support detail, which
 * authorizes `ride:view` at the ride's own market, so a market-scoped atendente reaches exactly its own rides.
 *
 * There is no execution `phase` here on purpose: the support detail carries the ride's durable status and its
 * typed event log with reason codes and policy versions, and inventing a phase from an event type would put a
 * value on the screen that no server produced.
 */
export interface OperatorRideTimeline {
  readonly rideId: string;
  readonly status: string;
  readonly marketId: string;
  /** The durable event log, ordered by sequence, with the whitelisted reason/policy fields. */
  readonly events: readonly RideSupportTimelineEntry[];
  readonly cancellationReason?: string;
}

/** Fold the ride-support detail into the operator timeline view (pure). */
export function operatorTimelineOf(detail: RideSupportDetail): OperatorRideTimeline {
  return {
    rideId: detail.ride.rideId,
    status: detail.ride.status,
    marketId: detail.ride.marketId,
    events: [...detail.timeline].sort((a, b) => a.sequence - b.sequence),
    ...(detail.ride.cancellationReason !== undefined ? { cancellationReason: detail.ride.cancellationReason } : {}),
  };
}

/** Read a ride's timeline as an operator, through the market-authorized ride-support route (D358/D364). */
export async function loadRideTimeline(runtime: { readonly rideSupport: RideSupportApi }, rideId: string): Promise<Result<OperatorRideTimeline, ApiError>> {
  const res = await runtime.rideSupport.detail(rideId);
  return res.ok ? { ok: true, value: operatorTimelineOf(res.value) } : res;
}

// ---- durable dashboard feed (server read model, S112) --------------------------------------------

/**
 * Load the scoped live dashboard from the server ops-read feed (KPIs + the active-ride queue). This is the
 * durable projection the console reconciles against; the realtime deltas ({@link subscribeOps}) fill the gaps
 * between reads. The operator's scope is passed so the server enforces containment (a city operator can never
 * pull another city's feed).
 */
export async function loadDashboard(runtime: { readonly ops: OpsApi }, scope: OperatorScope, markets: readonly OperatingMarketView[] = []): Promise<Result<OpsDashboardView, ApiError>> {
  if (scope.region !== undefined && scope.city === undefined) return loadRegionDashboard(runtime, scope, markets);
  return runtime.ops.dashboard({
    ...(scope.country !== undefined ? { country: scope.country } : {}),
    ...(scope.city !== undefined ? { city: scope.city } : {}),
  });
}

/** The most markets one state read fans out to (a state beyond it is read for its first markets - the screen is a live queue, not a report). */
export const REGION_READ_MAX_MARKETS = 20;

/**
 * The live dashboard of a STATE (ADR-0124): the ops feed has no region filter yet, and a state grant does not cover a country-wide
 * read, so the console reads each market of the state (a city target, which the state grant covers) and merges the answers - never the
 * whole country. Counters and the GMV add up; the rates the server computes per market (take rate, ETA, supply/demand) have no honest
 * sum, so they are named `unavailable` and the completion rate is recomputed from the merged counters. A state with no registered
 * market answers an empty feed.
 */
async function loadRegionDashboard(runtime: { readonly ops: OpsApi }, scope: OperatorScope, markets: readonly OperatingMarketView[]): Promise<Result<OpsDashboardView, ApiError>> {
  const ids = marketsOfRegion(scope.region ?? '', markets).map((m) => m.id).slice(0, REGION_READ_MAX_MARKETS);
  const answers = await Promise.all(ids.map((city) => runtime.ops.dashboard({ country: countryOfMarket(city), city })));
  const views: OpsDashboardView[] = [];
  for (const a of answers) {
    if (!a.ok) return a;
    views.push(a.value);
  }
  return { ok: true, value: mergeDashboards(views, scope) };
}

/** Merge the per-market dashboards of a state into one (see {@link loadRegionDashboard}). Exported for the tests. */
export function mergeDashboards(views: readonly OpsDashboardView[], scope: OperatorScope): OpsDashboardView {
  const sum = (pick: (v: OpsDashboardView) => number): number => views.reduce((n, v) => n + pick(v), 0);
  const requested = sum((v) => v.kpis.requested);
  const matched = sum((v) => v.kpis.matched);
  const completed = sum((v) => v.kpis.completed);
  const cancelled = sum((v) => v.kpis.cancelled);
  const unavailable = new Set<string>(['netRevenue', 'takeRateBps', 'avgEtaSeconds', 'supplyDemandRatio']);
  const real = views.filter((v) => !(v.unavailable ?? []).includes('gmv') && v.kpis.gmv.currency !== 'XTS' && v.kpis.gmv.currency !== 'XXX');
  const currencies = new Set(real.map((v) => v.kpis.gmv.currency));
  const gmvUnavailable = views.some((v) => (v.unavailable ?? []).includes('gmv')) || currencies.size > 1;
  if (gmvUnavailable) unavailable.add('gmv');
  const [currency] = [...currencies];
  const gmvMinor = real.reduce((n, v) => n + (/^-?\d+$/.test(v.kpis.gmv.amountMinor) ? BigInt(v.kpis.gmv.amountMinor) : 0n), 0n);
  for (const field of ['requested', 'matched', 'completed', 'cancelled'] as const) if (views.some((v) => (v.unavailable ?? []).includes(field))) unavailable.add(field);
  if (requested <= 0 || views.some((v) => (v.unavailable ?? []).includes('completionRate'))) unavailable.add('completionRate');
  return {
    scope: { ...(scope.country !== undefined ? { country: scope.country } : {}) },
    kpis: {
      requested,
      matched,
      completed,
      cancelled,
      fillRate: requested > 0 ? matched / requested : 0,
      completionRate: requested > 0 ? completed / requested : 0,
      gmv: { amountMinor: gmvMinor.toString(), currency: currency ?? 'XTS' },
      netRevenue: { amountMinor: '0', currency: currency ?? 'XTS' },
      takeRateBps: 0,
      avgEtaSeconds: 0,
      supplyDemandRatio: 0,
    },
    unavailable: [...unavailable],
    rides: views.flatMap((v) => v.rides),
  };
}

// ---- manual dispatch / reassign / cancel (server-audited) ----------------------------------------

/** Manual dispatch (or reassign) a ride to a driver with an operator reason; the server records the audit. */
export function manualDispatch(runtime: { readonly ops: OpsApi }, rideId: string, input: { readonly driverId: string; readonly reason: string }): Promise<Result<ManualDispatchResult, ApiError>> {
  return runtime.ops.manualDispatch(rideId, input);
}

/**
 * Cancel a ride as an operator with a reason code; the server scope-checks, transitions, and audits it (S112). The
 * optional note is INTERNAL: the server writes it to the operator audit only, never on the ride the rider, the driver
 * or the partner desk read (W5-HOTFIX, D382).
 */
export function cancelRide(runtime: { readonly ops: OpsApi }, rideId: string, input: OpsCancelInput): Promise<Result<OpsCancelResult, ApiError>> {
  return runtime.ops.cancelRide(rideId, input);
}

// D401: `subscribeOps` (the realtime ops channel subscription) had no caller — the console polls. Removed.
